import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDateShort, formatTime } from "@/lib/format";
import { CashTimeline } from "@/components/finance/CashTimeline";
import OpenCashDialog from "@/components/finance/OpenCashDialog";
import CloseCashDialog from "@/components/finance/CloseCashDialog";
import CashMovementDialog from "@/components/finance/CashMovementDialog";
import CashSessionDetailDialog from "@/components/finance/CashSessionDetailDialog";
import { Wallet, Loader2, ArrowDownCircle, ArrowUpCircle, Lock, Clock, User } from "lucide-react";
import { ExportButton } from "@/components/ExportButton";

export default function Caisse() {
  const { orgId, can } = useAuth();
  const [openDlg, setOpenDlg] = useState(false);
  const [closeDlg, setCloseDlg] = useState(false);
  const [movement, setMovement] = useState(null); // "CASH_DEPOSIT" | "CASH_WITHDRAWAL"
  const [detailId, setDetailId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["cash-current", orgId],
    queryFn: async () => (await api.get("/api/cash/current", { params: { organization_id: orgId } })).data,
    enabled: !!orgId,
  });

  const currency = data?.currency || "MAD";
  const session = data?.session;
  const manage = data?.can_manage && can("cash.manage");
  const m = (c) => formatMoney(c || 0, currency);

  if (isLoading) return <Center><Loader2 className="h-6 w-6 animate-spin text-primary" /></Center>;

  return (
    <div className="mx-auto max-w-[1400px] space-y-6 p-4 sm:p-6 lg:p-8" data-testid="caisse-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Caisse</h1>
        {can("finance.view") && <ExportButton kind="payments" orgId={orgId} label="Exporter paiements" testid="payments-export-button" />}
      </div>

      {!session ? (
        <ClosedState data={data} manage={manage} m={m} onOpen={() => setOpenDlg(true)} onSession={setDetailId} />
      ) : (
        <OpenState session={session} currency={currency} manage={manage} m={m}
          onDeposit={() => setMovement("CASH_DEPOSIT")} onWithdraw={() => setMovement("CASH_WITHDRAWAL")}
          onClose={() => setCloseDlg(true)} recent={data.recent} onSession={setDetailId} />
      )}

      <OpenCashDialog open={openDlg} onOpenChange={setOpenDlg} currency={currency} />
      {session && <CloseCashDialog open={closeDlg} onOpenChange={setCloseDlg} session={session} currency={currency} />}
      {session && <CashMovementDialog open={!!movement} onOpenChange={() => setMovement(null)} sessionId={session.id} type={movement || "CASH_DEPOSIT"} currency={currency} />}
      {detailId && <CashSessionDetailDialog sessionId={detailId} open={!!detailId} onOpenChange={() => setDetailId(null)} />}
    </div>
  );
}

function ClosedState({ data, manage, m, onOpen, onSession }) {
  const last = data?.last_closed;
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 text-center" data-testid="caisse-closed-state">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400"><Lock className="h-7 w-7" /></div>
        <h2 className="mt-5 text-lg font-semibold text-slate-900">Caisse fermée</h2>
        {last && <p className="mt-1 text-sm text-slate-500">Dernière fermeture : {formatDateShort(last.closed_at)} — {formatTime(last.closed_at)}</p>}
        {manage && (
          <button onClick={onOpen} data-testid="open-cash-button" className="mt-6 flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">
            <Wallet className="h-4 w-4" /> Ouvrir la caisse
          </button>
        )}
      </div>
      <SessionHistory recent={data?.recent} m={m} onSession={onSession} />
    </div>
  );
}

function OpenState({ session, currency, manage, m, onDeposit, onWithdraw, onClose, recent, onSession }) {
  const t = session.totals;
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5" data-testid="caisse-open-header">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Caisse ouverte
            </span>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400" /> Ouverte à {formatTime(session.opened_at)}</span>
              <span className="flex items-center gap-1.5"><User className="h-4 w-4 text-slate-400" /> {session.opened_by_name}</span>
              <span className="flex items-center gap-1.5"><Wallet className="h-4 w-4 text-slate-400" /> Fond : {m(t.opening_cash_cents)}</span>
            </div>
          </div>
          {manage && (
            <div className="flex flex-wrap gap-2">
              <button onClick={onDeposit} data-testid="deposit-button" className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><ArrowDownCircle className="h-4 w-4 text-emerald-600" /> Dépôt</button>
              <button onClick={onWithdraw} data-testid="withdraw-button" className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><ArrowUpCircle className="h-4 w-4 text-rose-600" /> Retrait</button>
              <button onClick={onClose} data-testid="close-cash-button" className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"><Lock className="h-4 w-4" /> Fermer la caisse</button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label="Espèces encaissées" value={m(t.cash_payments_cents)} testid="kpi-cash-payments" tone="emerald" />
        <Kpi label="Sorties espèces" value={m(t.cash_expenses_cents)} testid="kpi-cash-expenses" tone="rose" />
        <Kpi label="Autres entrées" value={m(t.deposits_cents)} testid="kpi-deposits" tone="emerald" />
        <Kpi label="Retraits" value={m(t.withdrawals_cents)} testid="kpi-withdrawals" tone="rose" />
        <Kpi label="Caisse théorique" value={m(t.expected_cash_cents)} testid="kpi-expected-cash" tone="primary" big className="col-span-2 lg:col-span-1" />
      </div>

      <div className="flex flex-wrap gap-3">
        <NonCash label="Carte encaissée" value={m(t.card_cents)} testid="kpi-card" />
        <NonCash label="Virement encaissé" value={m(t.transfer_cents)} testid="kpi-transfer" />
        {t.other_cents > 0 && <NonCash label="Autre encaissé" value={m(t.other_cents)} testid="kpi-other" />}
        <span className="self-center text-xs text-slate-400">Les paiements carte/virement n'entrent pas dans la caisse physique.</span>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">Mouvements de la caisse</h3>
          <CashTimeline events={session.timeline} currency={currency} />
        </div>
        <div><SessionHistory recent={recent} m={m} onSession={onSession} /></div>
      </div>
    </div>
  );
}

function SessionHistory({ recent, m, onSession }) {
  const rows = (recent || []).filter((s) => s.status === "CLOSED");
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="cash-history">
      <h3 className="mb-3 text-sm font-semibold text-slate-700">Historique des caisses</h3>
      {rows.length === 0 ? <p className="text-sm text-slate-400">Aucune caisse fermée pour l'instant.</p> : (
        <div className="divide-y divide-slate-100">
          {rows.map((s) => (
            <button key={s.id} onClick={() => onSession(s.id)} data-testid={`cash-session-row-${s.id}`}
              className="flex w-full items-center justify-between py-2.5 text-left text-sm hover:bg-slate-50">
              <div>
                <p className="font-medium text-slate-800">{formatDateShort(s.opened_at)}</p>
                <p className="text-xs text-slate-400">{formatTime(s.opened_at)} → {formatTime(s.closed_at)} · {s.opened_by_name}</p>
              </div>
              <div className="text-right">
                <p className="font-mono font-medium text-slate-800">{m(s.actual_closing_cash_cents)}</p>
                <p className={`text-xs font-medium ${s.closing_difference_cents === 0 ? "text-emerald-600" : "text-amber-600"}`}>
                  Écart {s.closing_difference_cents > 0 ? "+" : ""}{m(s.closing_difference_cents)}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const TONE = { emerald: "text-emerald-700", rose: "text-rose-700", primary: "text-primary", slate: "text-slate-900" };
function Kpi({ label, value, testid, tone = "slate", big, className = "" }) {
  return (
    <div className={`rounded-2xl border bg-white p-4 ${tone === "primary" ? "border-primary/30 ring-1 ring-primary/10" : "border-slate-200"} ${className}`} data-testid={testid}>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1.5 font-mono font-bold ${big ? "text-2xl" : "text-xl"} ${TONE[tone]}`}>{value}</p>
    </div>
  );
}
const NonCash = ({ label, value, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-testid={testid}>
    <span className="text-xs text-slate-400">{label} : </span><span className="font-mono text-sm font-semibold text-slate-700">{value}</span>
  </div>
);
const Center = ({ children }) => <div className="flex h-[60vh] items-center justify-center">{children}</div>;
